import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useDucatClient } from '@/context/DucatClientCtx';
import { useVaultProfile } from '@/hooks/react/useVaultProfile';
import { useAppStore, useConfig } from '@/stores/useAppStore';

import { quantaHeadersBelongToWallet } from './quantaIdentity';
import { claimVaultAutomatically, proveVaultClaim } from './vaultClaim';

export function useQuantaVaultClaim(userId?: string) {
  const { wallet, client, accountRevision } = useDucatClient();
  const { vaultTxId } = useVaultProfile({ purpose: 'quanta' });
  const headers = useAppStore((s) => s.quantaHeaders);
  const config = useConfig();
  const chain = String(config && ('chainNetwork' in config ? config.chainNetwork : config.network));
  const network = ['main', 'mainnet', 'alpha-mainnet', 'mainnet-alpha'].includes(chain) ? 'mainnet' : 'testnet';
  const current = useRef({ wallet, accountRevision, userId, vaultTxId, network });
  current.current = { wallet, accountRevision, userId, vaultTxId, network };
  const mounted = useRef(true);
  const approving = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const queryClient = useQueryClient();
  const queryKey = [
    'quanta-vault-claim',
    userId,
    vaultTxId,
    headers?.['x-address'],
    headers?.['x-user-public-key'],
    accountRevision,
    network,
  ];
  const enabled = !!userId && !!vaultTxId && !!headers && quantaHeadersBelongToWallet(headers, wallet, accountRevision);
  const assertCurrent = () => {
    if (
      !mounted.current ||
      current.current.network !== network ||
      !enabled ||
      current.current.wallet !== wallet ||
      current.current.accountRevision !== accountRevision ||
      current.current.userId !== userId ||
      current.current.vaultTxId !== vaultTxId ||
      useAppStore.getState().quantaHeaders !== headers
    ) {
      throw new Error('Wallet changed during vault ownership verification');
    }
  };
  const query = useQuery({
    queryKey,
    enabled,
    retry: false,
    staleTime: Infinity,
    queryFn: async () => {
      assertCurrent();
      if (!userId || !vaultTxId || !headers) throw new Error('Wallet unavailable');
      const result = await claimVaultAutomatically(userId, vaultTxId, headers, network);
      assertCurrent();
      return result;
    },
  });
  const approval = useMutation({
    mutationFn: async () => {
      if (approving.current) return;
      approving.current = true;
      try {
        assertCurrent();
        if (!wallet || !headers || !userId || !vaultTxId || !config) throw new Error('Wallet unavailable');
        const rawKey = wallet.account.vault.pubkey;
        await proveVaultClaim(
          {
            userId,
            vaultId: vaultTxId,
            origin: window.location.origin,
            network,
            vaultAddress: wallet.account.vault.address,
            vaultPublicKey: rawKey.length === 66 ? rawKey.slice(2) : rawKey,
          },
          headers,
          async (address, message) => {
            const result = await client.signMessage({ address, message });
            return result?.result?.signature ?? '';
          },
          assertCurrent,
        );
        queryClient.setQueryData(queryKey, { claimed: true });
        await queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('quanta/') });
      } finally {
        approving.current = false;
      }
    },
  });
  return { ...query, approval, vaultTxId, network, canReadVault: enabled && query.data?.claimed === true };
}
